<template>
  <div class="clinics-page">
    <div class="vm-page-header">
      <div>
        <h1>Clínicas</h1>
        <span class="vm-page-header__sub">
          Convites recebidos e clínicas onde você atende
        </span>
      </div>
    </div>

    <!-- Carregando -->
    <div
      v-if="state === 'loading'"
      class="card clinics-card"
      data-test="loading"
    >
      <div v-for="n in 3" :key="n" class="skel clinics-skel" />
    </div>

    <!-- Erro -->
    <div
      v-else-if="state === 'error'"
      class="card clinics-card"
      data-test="error"
    >
      <div class="vm-empty">
        <div class="vm-empty-ill clinics-ill--danger">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <h3>Não foi possível carregar suas clínicas</h3>
        <p>Verifique sua conexão e tente novamente.</p>
        <button
          class="btn btn--primary btn--sm"
          data-test="retry"
          @click="load"
        >
          Tentar novamente
        </button>
      </div>
    </div>

    <template v-else>
      <!-- Convites pendentes -->
      <div class="card clinics-card">
        <div class="clinics-card__head">
          <h3>Convites pendentes</h3>
          <span
            v-if="invitations.length"
            class="badge badge--warning badge--no-dot"
          >
            {{ invitations.length }}
          </span>
        </div>

        <p v-if="!invitations.length" class="clinics-empty">
          Nenhum convite pendente
        </p>

        <div
          v-for="inv in invitations"
          :key="inv.id"
          class="invite-row"
          data-test="invite-row"
        >
          <span class="avatar avatar--accent">{{ initial(inv.clinic) }}</span>
          <div class="invite-row__info">
            <span class="invite-row__name">{{ inv.clinic.name }}</span>
            <span class="invite-row__meta">
              {{ inviteMeta(inv) }}
            </span>
          </div>
          <div class="invite-row__actions">
            <button
              class="btn btn--outline btn--sm"
              data-test="decline"
              :disabled="busyId === inv.id"
              @click="askDecline(inv)"
            >
              Recusar
            </button>
            <button
              class="btn btn--primary btn--sm"
              data-test="accept"
              :disabled="busyId === inv.id"
              @click="accept(inv)"
            >
              {{ busyId === inv.id ? "Aguarde…" : "Aceitar" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Onde você atende -->
      <div class="card clinics-card">
        <div class="clinics-card__head">
          <h3>Onde você atende</h3>
        </div>

        <p v-if="!clinics.length" class="clinics-empty">
          Você ainda não atende por nenhuma clínica
        </p>

        <table v-else class="table">
          <thead>
            <tr>
              <th>Clínica</th>
              <th>Unidade</th>
              <th>Repasse</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in clinics"
              :key="row.clinic.id"
              data-test="clinic-row"
            >
              <td class="cell-strong">{{ row.clinic.name }}</td>
              <td>{{ row.unit ? row.unit.name : "—" }}</td>
              <td class="num">
                {{ row.transfer_pct ? `${row.transfer_pct}%` : "—" }}
              </td>
              <td>
                <span v-if="row.own" class="badge badge--info">
                  Sua clínica
                </span>
                <span v-else class="badge badge--success">Ativo</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Confirmação de recusa -->
    <b-modal v-model="showDecline" title="Recusar convite" hide-footer centered>
      <p v-if="declining" class="clinics-modal__msg">{{ declineMessage }}</p>
      <div class="clinics-modal__actions">
        <button
          class="btn btn--outline btn--sm"
          :disabled="!!busyId"
          @click="showDecline = false"
        >
          Cancelar
        </button>
        <button
          class="btn btn--danger btn--sm"
          data-test="confirm-decline"
          :disabled="!!busyId"
          @click="decline"
        >
          {{ busyId ? "Aguarde…" : "Recusar" }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { DoctorClinics } from "@/services/doctor_clinics";

// Painel do médico › Clínicas: convites de clínica (aceitar/recusar sem
// depender do e-mail) e clínicas onde ele atende. US-C2.
export default {
  name: "DoctorClinicsView",
  data() {
    return {
      state: "loading",
      invitations: [],
      clinics: [],
      busyId: null,
      declining: null,
      showDecline: false
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.state = "loading";
      try {
        const [inv, cli] = await Promise.all([
          DoctorClinics.invitations(),
          DoctorClinics.clinics()
        ]);
        this.invitations = inv.data || [];
        this.clinics = cli.data || [];
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    async accept(inv) {
      if (this.busyId) return;
      this.busyId = inv.id;
      try {
        const { data } = await DoctorClinics.accept(inv.id);
        this.removeInvitation(inv);
        this.clinics = [
          ...this.clinics.filter(c => c.clinic.id !== data.clinic.id),
          data
        ];
        this.$toast.success(`Agora você faz parte da ${inv.clinic.name}`);
        this.$store.dispatch("clinicInvitations/refresh");
      } catch (e) {
        this.$toast.error(this.errorMessage(e));
      } finally {
        this.busyId = null;
      }
    },
    askDecline(inv) {
      this.declining = inv;
      this.showDecline = true;
    },
    async decline() {
      const inv = this.declining;
      if (!inv || this.busyId) return;
      this.busyId = inv.id;
      try {
        await DoctorClinics.decline(inv.id);
        this.removeInvitation(inv);
        this.showDecline = false;
        this.$toast.success("Convite recusado");
        this.$store.dispatch("clinicInvitations/refresh");
      } catch (e) {
        this.$toast.error(this.errorMessage(e));
      } finally {
        this.busyId = null;
      }
    },
    removeInvitation(inv) {
      this.invitations = this.invitations.filter(i => i.id !== inv.id);
    },
    errorMessage(error) {
      const status = error && error.response && error.response.status;
      return status === 404
        ? "Este convite não está mais disponível."
        : "Não foi possível concluir. Tente novamente.";
    },
    initial(clinic) {
      return (clinic.name || "?")
        .trim()
        .charAt(0)
        .toUpperCase();
    },
    inviteMeta(inv) {
      return [
        inv.unit && inv.unit.name,
        inv.transfer_pct && `Repasse ${inv.transfer_pct}%`,
        inv.invited_by_name && `Convidado por ${inv.invited_by_name}`,
        inv.created_at && format(parseISO(inv.created_at), "dd/MM/yyyy")
      ]
        .filter(Boolean)
        .join(" · ");
    }
  },
  computed: {
    declineMessage() {
      return `Recusar o convite da ${this.declining.clinic.name}? A clínica verá o convite como recusado.`;
    }
  }
};
</script>

<style lang="scss" scoped>
.clinics-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.clinics-card {
  padding: var(--space-6);

  &__head {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-4);

    h3 {
      margin: 0;
      font-size: var(--font-size-lg);
      font-weight: var(--fw-bold);
      color: var(--color-gray-900);
    }
  }
}

.clinics-skel {
  height: 56px;
  margin-bottom: var(--space-3);
}

.clinics-ill--danger {
  background: var(--color-danger-light);
  color: var(--color-danger);
}

.clinics-empty {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.invite-row {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) 0;
  border-top: 1px solid var(--color-gray-100);

  &:first-of-type {
    border-top: none;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }

  &__meta {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
  }

  &__actions {
    display: flex;
    gap: var(--space-2);
  }
}

.clinics-modal__msg {
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
}

.clinics-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

@media (max-width: 860px) {
  .invite-row {
    flex-wrap: wrap;

    &__actions {
      width: 100%;

      .btn {
        flex: 1;
      }
    }
  }
}
</style>
